/**
 * EC / HDB buyer eligibility — shared by the Buying and Affordability calculators so the
 * two never drift apart.
 *
 * Each check returns `blockers` (the buyer cannot buy / borrow this way) and `notes`
 * (conditions the inputs can't verify, shown as advisories).
 *
 * Income ceilings are eligibility tests on gross monthly household income. They never cap
 * the income MSR / TDSR are assessed on.
 */
import { CITIZENSHIP_LABELS, type CitizenshipType, type PropertyCount } from '../../utils/stampDutyRates';

// ─── EC (bought from the developer) ──────────────────────────────────────────
// https://www.hdb.gov.sg/buying-a-flat/executive-condominiums/eligibility
//   • Married couple (Public / Fiancé-Fiancée Scheme): ≥1 SC, co-applicant SC or PR,
//     everyone ≥ 21.
//   • Singles: only under the Joint Singles Scheme (with other singles), all SC, all ≥ 35.
//     A lone single cannot buy a new EC.
//   • Household income ceiling $16,000 ($18,000 for EC land tenders closing on or after
//     24 Aug 2026).
export const EC_INCOME_CEILING = 16000;
export const EC_INCOME_CEILING_NEW_SITES = 18000;
export const EC_ELIGIBILITY_URL = 'https://www.hdb.gov.sg/buying-a-flat/executive-condominiums/eligibility';

// ─── HDB concessionary loan ──────────────────────────────────────────────────
// Raised from $14,000 / $7,000 with effect from 24 Aug 2026 (National Day Rally,
// 23 Aug 2026). A lone single uses the Single Singapore Citizen Scheme ceiling; joint
// applicants (couples or Joint Singles) use the family ceiling. The extended family
// ceiling needs 3+ members and isn't modelled — the calculators take up to two borrowers.
// https://www.hdb.gov.sg/buying-a-flat/flat-grant-and-loan-eligibility/housing-loan/housing-loan-from-hdb
export const HDB_LOAN_INCOME_CEILING_FAMILY = 16000;
export const HDB_LOAN_INCOME_CEILING_SINGLE = 8000;
export const HDB_LOAN_ELIGIBILITY_URL = 'https://www.hdb.gov.sg/buying-a-flat/flat-grant-and-loan-eligibility/housing-loan/housing-loan-from-hdb';
/** HDB loan terms: 2.6% concessionary rate, up to 75% LTV, 25 years and repaid by age 65. */
export const HDB_CONCESSIONARY_INTEREST_RATE = 2.6;
export const HDB_LOAN_LTV = 75;
export const HDB_LOAN_MAX_TENURE = 25;
export const HDB_LOAN_MAX_AGE = 65;

// ─── HDB resale flat ─────────────────────────────────────────────────────────
// An all-SPR household can buy a resale flat under the Public Scheme once every
// applicant has held SPR status for at least 3 years; no CPF Housing Grants and no HDB
// loan (both need an SC). Anyone who already owns a property is treated as not eligible.
export const HDB_SPR_MIN_YEARS = 3;
export const HDB_RESALE_ELIGIBILITY_URL = 'https://www.hdb.gov.sg/residential/buying-a-flat/understanding-your-eligibility-and-housing-loan-options/flat-and-grant-eligibility/couples-and-families';

export type BuyerRelationship = 'SPOUSE' | 'INDIVIDUAL';

export interface EligibilityApplicant {
  label: string;
  age: number;
  citizenship: CitizenshipType;
  /** Which purchase this would be for the applicant — '2nd' or later means they already own one. */
  propertyCount: PropertyCount;
}

export interface EligibilityInput {
  /** Primary first; one entry = a lone single buyer. */
  applicants: EligibilityApplicant[];
  /** Joint purchases only: married couple or Joint Singles. Ignored for a lone buyer. */
  relationship: BuyerRelationship;
  /** Gross monthly household income — before any self-employed / variable-income haircut. */
  grossMonthlyIncome: number;
}

export interface EligibilityResult {
  eligible: boolean;
  /** Scheme the buyers fall under, e.g. "Joint Singles Scheme"; '' when none fits. */
  scheme: string;
  blockers: string[];
  notes: string[];
}

export interface IncomeCeilingResult extends EligibilityResult {
  incomeCeiling: number;
  incomeExceeded: boolean;
}

const money = (n: number): string => `$${Math.round(n).toLocaleString('en-SG')}`;
const listAges = (list: EligibilityApplicant[]) => list.map((a) => `${a.label} is ${a.age}`).join('; ');
const listCitizenship = (list: EligibilityApplicant[]) =>
  list.map((a) => `${a.label} is ${CITIZENSHIP_LABELS[a.citizenship]}`).join('; ');
const isJointSingles = (input: EligibilityInput) =>
  input.applicants.length > 1 && input.relationship === 'INDIVIDUAL';
const ownsProperty = (input: EligibilityInput) => input.applicants.filter((a) => a.propertyCount !== '1st');

const result = <T extends object>(scheme: string, blockers: string[], notes: string[], extra: T) => ({
  eligible: blockers.length === 0,
  scheme,
  blockers,
  notes,
  ...extra,
});

/** Joint Singles Scheme — every applicant a Singapore Citizen aged 35 or over. */
const jointSinglesBlockers = (applicants: EligibilityApplicant[]): string[] => {
  const blockers: string[] = [];
  const nonSC = applicants.filter((a) => a.citizenship !== 'SC');
  if (nonSC.length > 0) {
    blockers.push(`Joint Singles Scheme: all applicants must be Singapore Citizens — ${listCitizenship(nonSC)}.`);
  }
  const underAge = applicants.filter((a) => a.age < 35);
  if (underAge.length > 0) {
    blockers.push(`Joint Singles Scheme: all applicants must be at least 35 years old — ${listAges(underAge)}.`);
  }
  return blockers;
};

const minAgeBlockers = (applicants: EligibilityApplicant[]): string[] => {
  const underAge = applicants.filter((a) => a.age < 21);
  return underAge.length > 0 ? [`All applicants must be at least 21 years old — ${listAges(underAge)}.`] : [];
};

/** New EC bought from the developer. */
export function assessEcEligibility(input: EligibilityInput): IncomeCeilingResult {
  const { applicants, grossMonthlyIncome } = input;
  const blockers: string[] = [];
  const notes: string[] = [];
  let scheme = '';

  const incomeExceeded = grossMonthlyIncome > EC_INCOME_CEILING;
  if (incomeExceeded) {
    blockers.push(
      `Gross household income ${money(grossMonthlyIncome)}/mo is above the ${money(EC_INCOME_CEILING)} EC income ceiling`
        + (grossMonthlyIncome <= EC_INCOME_CEILING_NEW_SITES
          ? ` (ECs from land tenders closing on or after 24 Aug 2026 allow up to ${money(EC_INCOME_CEILING_NEW_SITES)}).`
          : '.'),
    );
  }

  if (applicants.length === 1) {
    blockers.push('A single cannot buy a new EC alone — singles qualify only under the Joint Singles Scheme, buying with another single Singapore Citizen aged 35 or over.');
  } else if (isJointSingles(input)) {
    scheme = 'Joint Singles Scheme';
    blockers.push(...jointSinglesBlockers(applicants));
  } else {
    scheme = 'Public Scheme';
    if (!applicants.some((a) => a.citizenship === 'SC')) {
      blockers.push('Married couple: at least one applicant must be a Singapore Citizen.');
    }
    const foreigners = applicants.filter((a) => a.citizenship === 'FOREIGNER');
    if (foreigners.length > 0) {
      blockers.push(`Married couple: the co-applicant must be a Singapore Citizen or PR — foreigners are not eligible (${foreigners.map((a) => a.label).join('; ')}).`);
    }
    blockers.push(...minAgeBlockers(applicants));
  }

  if (ownsProperty(input).length > 0) {
    notes.push('Already owns a property: must not own, or have sold in the last 30 months, a private property; an existing HDB flat must be sold within 6 months of key collection.');
  }

  return result(scheme, blockers, notes, { incomeCeiling: EC_INCOME_CEILING, incomeExceeded });
}

/** HDB resale flat — the buyer profile (scheme, citizenship, age, existing property). */
export function assessHdbFlatEligibility(input: EligibilityInput): EligibilityResult {
  const { applicants } = input;
  const blockers: string[] = [];
  const notes: string[] = [];
  let scheme = '';

  const owners = ownsProperty(input);
  if (owners.length > 0) {
    blockers.push(`Already owns a property (${owners.map((a) => `${a.label}: ${a.propertyCount} purchase`).join('; ')}) — an HDB flat cannot be bought as a second property. Sell the existing one first.`);
  }

  const hasSC = applicants.some((a) => a.citizenship === 'SC');
  const allSpr = applicants.every((a) => a.citizenship === 'SPR');

  if (applicants.length === 1) {
    scheme = 'Single Singapore Citizen Scheme';
    const [buyer] = applicants;
    if (buyer.citizenship !== 'SC') {
      blockers.push(`Single Singapore Citizen Scheme: a single buyer must be a Singapore Citizen — ${listCitizenship([buyer])}.`);
    } else if (buyer.age < 35) {
      blockers.push(`Single Singapore Citizen Scheme: a single buyer must be at least 35 years old (orphans may apply from 21) — ${buyer.label} is ${buyer.age}.`);
    }
  } else if (isJointSingles(input)) {
    scheme = 'Joint Singles Scheme';
    blockers.push(...jointSinglesBlockers(applicants));
  } else {
    if (!hasSC && !allSpr) {
      blockers.push('Married couple: must include a Singapore Citizen, or be an all-SPR household (every applicant an SPR).');
    }
    const hasForeigner = applicants.some((a) => a.citizenship === 'FOREIGNER');
    if (hasSC && hasForeigner) {
      scheme = 'Non-citizen Spouse Scheme';
      const scAge = applicants.find((a) => a.citizenship === 'SC')?.age ?? 0;
      notes.push(scAge >= 35
        ? 'Non-citizen Spouse Scheme: the non-citizen spouse must hold a valid Visit Pass or Work Pass.'
        : 'Non-citizen Spouse Scheme: the non-citizen spouse must hold a valid Long-Term Visit Pass or Work Pass (Singapore Citizen applicant is 21–34). With a Singapore Citizen child, you may buy under the Public Scheme instead.');
    } else if (hasSC) {
      scheme = 'Public Scheme';
    } else if (allSpr) {
      scheme = 'Public Scheme — SPR household';
      notes.push(`Every applicant must have held SPR status for at least ${HDB_SPR_MIN_YEARS} continuous years. No CPF Housing Grants.`);
    }
    blockers.push(...minAgeBlockers(applicants));
  }

  return result(scheme, blockers, notes, {});
}

/** HDB concessionary loan — on top of the flat eligibility above. */
export function assessHdbLoanEligibility(input: EligibilityInput): IncomeCeilingResult & { isSingle: boolean } {
  const { applicants, grossMonthlyIncome } = input;
  const blockers: string[] = [];
  const isSingle = applicants.length === 1;
  const incomeCeiling = isSingle ? HDB_LOAN_INCOME_CEILING_SINGLE : HDB_LOAN_INCOME_CEILING_FAMILY;

  if (!applicants.some((a) => a.citizenship === 'SC')) {
    blockers.push('HDB loan: at least one buyer must be a Singapore Citizen — bank loan only.');
  }
  const incomeExceeded = grossMonthlyIncome > incomeCeiling;
  if (incomeExceeded) {
    blockers.push(`HDB loan: gross household income ${money(grossMonthlyIncome)}/mo is above the ${money(incomeCeiling)} ${isSingle ? 'singles' : 'family'} ceiling — bank loan only.`);
  }

  return result('', blockers, [], { incomeCeiling, incomeExceeded, isSingle });
}
